"use client";

import { useEffect, useState } from "react";
import { track } from "@/lib/telemetry/track";

function generateClientErrorId() {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `c-${ts}-${rand}`;
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [errorId] = useState(() => error.digest ?? generateClientErrorId());

  useEffect(() => {
    console.error(`Global error [${errorId}]:`, error);
    // A render-phase crash that hit the root boundary — the most severe "stuck": the user
    // sees a full-screen error. Record it for the weekly review (flushes on the navigation
    // that follows when they click Try again / Go home).
    track("client_error", {
      name: "react_boundary",
      metadata: { message: (error.message || "Render error").slice(0, 200), error_id: errorId },
    });
  }, [error, errorId]);

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, -apple-system, sans-serif" }}>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(to bottom right, #fefce8, #fff7ed, #f3e8ff)",
            padding: "1rem",
          }}
        >
          <div style={{ maxWidth: "28rem", textAlign: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem",
                marginBottom: "2rem",
              }}
            >
              <img
                src="/a4uLogo.png"
                alt="Admin4You"
                style={{ height: "3rem", width: "3rem", borderRadius: "0.75rem", objectFit: "cover" }}
              />
              <h1
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#1f2937",
                  fontFamily: "Cormorant Garamond, serif",
                  margin: 0,
                }}
              >
                Admin4You
              </h1>
            </div>

            <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1f2937", marginBottom: "0.75rem" }}>
              Something went wrong
            </h2>
            <p style={{ color: "#4b5563", marginBottom: "1.5rem", lineHeight: 1.6 }}>
              Try again, or email{" "}
              <a href="mailto:sales@admin-4you.com" style={{ color: "#0d9488" }}>
                sales@admin-4you.com
              </a>{" "}
              with the details below.
            </p>

            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "0.5rem",
                padding: "0.875rem 1rem",
                marginBottom: "1.5rem",
                textAlign: "left",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.75rem",
                color: "#374151",
                wordBreak: "break-word",
              }}
            >
              <div style={{ color: "#6b7280", marginBottom: "0.25rem" }}>Error ID</div>
              <div style={{ marginBottom: "0.75rem" }}>{errorId}</div>
              {error.message && (
                <>
                  <div style={{ color: "#6b7280", marginBottom: "0.25rem" }}>Message</div>
                  <div>{error.message}</div>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginTop: "1.5rem" }}>
              <button
                onClick={reset}
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "#0d9488",
                  color: "white",
                  border: "none",
                  borderRadius: "0.5rem",
                  fontSize: "1rem",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                Try again
              </button>
              <a
                href="/"
                style={{
                  padding: "0.75rem 1.5rem",
                  background: "white",
                  color: "#1f2937",
                  border: "1px solid #e5e7eb",
                  borderRadius: "0.5rem",
                  fontSize: "1rem",
                  fontWeight: 500,
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
